import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Calendar } from 'lucide-react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import BookCover from '../components/BookCover';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GrandmaProfile = () => {
  const { grandmaId } = useParams();
  const navigate = useNavigate();
  const [grandma, setGrandma] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGrandmaData();
  }, [grandmaId]);

  const fetchGrandmaData = async () => {
    try {
      const [grandmaRes, storiesRes] = await Promise.all([
        axios.get(`${API}/grandmas/${grandmaId}`),
        axios.get(`${API}/stories?approved_only=true`),
      ]);
      
      setGrandma(grandmaRes.data);
      // Filter stories by this grandma
      const grandmaStories = storiesRes.data.filter(s => s.grandma_id === grandmaId);
      setStories(grandmaStories);
    } catch (error) {
      console.error('Failed to fetch grandma data:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="grandma-profile-loading">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="w-32 h-32 rounded-full mx-auto mb-4" />
          <Skeleton className="w-48 h-8 mx-auto mb-2" />
          <Skeleton className="w-64 h-20 mx-auto" />
        </div>
      </div>
    );
  }

  if (!grandma) {
    return null;
  }

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="grandma-profile-page">
      <SEO path={`/grandma/${grandmaId}`} />
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Back button */}
          <Button
            data-testid="back-to-library"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug naar bibliotheek
          </Button>

          {/* Profile header */}
          <div className="text-center mb-12">
            <Avatar className="w-32 h-32 mx-auto mb-4 border-4 border-gold/30 shadow-xl">
              <AvatarImage src={grandma.photo_url} alt={grandma.name} />
              <AvatarFallback className="bg-old-rose text-night-blue text-4xl font-display">
                {grandma.name?.charAt(0) || '👵'}
              </AvatarFallback>
            </Avatar>

            <h1 className="font-display text-4xl text-foreground mb-2">
              {grandma.name}
            </h1>

            <p className="text-muted-foreground text-lg max-w-lg mx-auto font-body leading-relaxed">
              {grandma.bio}
            </p>

            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span className="font-accent">
                Lid sinds {new Date(grandma.created_at).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
              </span>
            </div>
          </div>

          {/* Stories section */}
          <div>
            <h2 className="font-display text-2xl text-foreground mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-gold" />
              Verhalen van {grandma.name}
            </h2>

            {stories.length === 0 ? (
              <div className="text-center py-12 bg-card/50 rounded-2xl">
                <div className="text-5xl mb-4">📚</div>
                <p className="text-muted-foreground font-accent">
                  Nog geen verhalen gepubliceerd
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                {stories.map((story, index) => (
                  <BookCover key={story.id} story={story} index={index} />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GrandmaProfile;
